import { useEffect, useRef } from 'react'
import { useSettingsState } from '~/atoms/SettingsState'
import { useSipInstance } from '~/atoms/SipInstanceState'
import { useOrphanedDeskphoneCleanup } from '~/hooks/useOrphanedDeskphoneCleanup'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { isElectron } from '~/utils/common'

/**
 * HybridCallManager - Handles WebSocket call events for desk phone integration
 *
 * This component ensures WebSocket events are processed even when:
 * - No SIP calls are active (desk phone only scenario)
 * - IncomingCallModal is not rendered
 * - User has call pop experiment enabled
 */
export const HybridCallManager = () => {
  const {
    sipInstance: [getSipInstance],
  } = useSipInstance()
  const sipClient = getSipInstance()
  const sipConnected = sipClient?.isConnected() ?? false

  const {
    actions: { dismissCall },
  } = usePhoneEndpoint({})

  // Automatic orphaned-deskphone-call cleanup (watchdog + reconnect sweep).
  // Mounted here (always-mounted) rather than usePhoneHub so deskphone-only
  // users — webphone disabled — still get the safety net. Flag-gated internally.
  useOrphanedDeskphoneCleanup()

  const {
    settings: {
      webPhone: { isEnabled: webPhoneEnabled },
      callCenter: { isEnabled: isCallCenterEnabled } = { isEnabled: false },
    },
  } = useSettingsState()

  const [, setSipConnected] = useSharedVariable<boolean>('SipConnected')

  // Sync SIP connection status to shared variables for popup access
  // For Call Center desk phone users: report as connected when webphone is disabled
  // This allows popup buttons to show for desk phone call control
  useEffect(() => {
    const effectiveSipConnected = isCallCenterEnabled
      ? !webPhoneEnabled || sipConnected
      : webPhoneEnabled && sipConnected
    setSipConnected(effectiveSipConnected)
  }, [sipConnected, webPhoneEnabled, isCallCenterEnabled, setSipConnected])

  // Sole registration for popup-dismiss-call IPC. Registered here (always
  // mounted) rather than IncomingCallModal so deskphone calls can be dismissed
  // even when the webphone is disabled.
  //
  // dismissCall from usePhoneEndpoint isn't referentially stable, so we hold it
  // in a ref and subscribe once. Cleanup uses removeAllListeners because the
  // preload's `receive` wraps the callback in a new function — removeListener
  // with our reference would never match the registered wrapper.
  const dismissCallRef = useRef(dismissCall)
  dismissCallRef.current = dismissCall

  useEffect(() => {
    if (!isElectron) return

    window.electron?.receive('popup-dismiss-call', (callUuid: string) => {
      dismissCallRef.current(callUuid)
    })

    return () => {
      window.electron?.removeAllListeners?.('popup-dismiss-call')
    }
  }, [])

  return null
}
